import React, {useEffect, useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {PentaSplit} from "../../components/common/PentaSplit";
import {
	Autocomplete,
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack, TextField,
	Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {graphQLServices} from "../../services/GraphQLServices";
import {
	APPROVE_TRANSLATIONS, DELETE_TRANSLATION_ITEMS,
	EXISTS_TRANSLATION_ITEM,
	GET_TRANSLATION_ITEMS, MODULES_INDEX, PHRASES_INDEX,
	PUT_TRANSLATION_ITEM
} from "./Queries";
import {formatDateTimeShort, GlobalState} from "../../global/GlobalState";
import {textColumn, tooltipColumn} from "../../components/common/grid/Columns";
import {DataGridPro, GridColDef, GridPagination} from "@mui/x-data-grid-pro";
import {cloneMutationData} from "../products/Utils";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {TranslationItem} from "./models/TranslationItem";
import {translationsEditorTargetStage} from "./TranslationsOverview";
import {ApolloQueryResult, useQuery} from "@apollo/client";
import {Language, Languages} from "./Data";
import {Module} from "./models/Module";
import {PhraseItem} from "./models/PhraseItem";
import {linkPhraseItemDetailsFor} from "./TranslationItemDetails";
import {translateFunction} from "../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../components/onsite/Footer";
import {EnvironmentStage} from "../../data/EnvironmentStage";
import {GridRowSelectionModel} from "@mui/x-data-grid/models/gridRowSelectionModel";
import {GridRowId} from "@mui/x-data-grid/models/gridRows";
import {targetStageSafetyCheck} from "../../libraries/util/SafetyCheck";
import {Instant} from "@js-joda/core";

const translate = translateFunction("SureBasePortalTranslations");
export const TranslationItemOverview = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={translationsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/translations_setup">{translate("Translation Setup")}</Link>
				<Typography color="text.primary">{translate("Translations")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<TranslationItemContent/>
			</Paper>}
		/>
	</>;
}, ["Admin", "Translations"]);

export function TranslationItemContent() {
	const [modulesLookup, setModulesLookup] = useState<Map<string, string>>();
	const [phrasesLookup, setPhrasesLookup] = useState<Map<string, string>>();
	const [resetKey, setResetKey] = useState(0);
	const [modules, setModules] = useState<Module[]>();
	const [phrases, setPhrases] = useState<PhraseItem[]>();
	const [rows, setRows] = useState<TranslationItem[]>();
	
	const updateGrid = () => setResetKey(resetKey + 1);
	const [rowSelectionModel, setRowSelectionModel] =
		React.useState<GridRowSelectionModel>([]);
	
	function onSelectionModelChange(selectionModel: GridRowSelectionModel) {
		setRowSelectionModel(selectionModel);
	}
	
	const stage = GlobalState.targetKiziStage(translationsEditorDefaultTargetStage);
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: "",
			renderCell: (params) => (
				<Link to={linkPhraseItemDetailsFor(params.row.id)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
			hideable: false,
		},
		{
			field: "language",
			headerName: translate("Language"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
		},
		{
			field: "module",
			headerName: translate("Module"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
		},
		{
			field: "phrase",
			headerName: translate("Phrase"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false
		},
		{
			field: "translation",
			headerName: translate("Translation"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "approvalDate",
			headerName: translate("Approval Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1
		}
		/* todo access */
	];
	
	
	const modulesIndex = useQuery(MODULES_INDEX, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
		}
	});
	
	const phrasesIndex = useQuery(PHRASES_INDEX, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
		}
	});
	
	if (!modulesIndex.loading && !modulesLookup) {
		const nodes: Module[] = modulesIndex.data.modules.nodes;
		setModules(nodes);
		setModulesLookup(nodes.associateBy(it => it.id, it => it.name));
	}
	
	if (!phrasesIndex.loading && !phrasesLookup) {
		const nodes: PhraseItem[] = phrasesIndex.data.phraseItems.nodes;
		setPhrases(nodes);
		setPhrasesLookup(nodes.associateBy(it => it.id, it => it.phrase));
	}
	
	useEffect(() => {
		if (!phrasesIndex.loading && !modulesIndex.loading && phrasesLookup && modulesLookup)
			getTranslationItems().then((theRows) => {
				theRows.forEach(item => {
					item.module = getLabel(item.moduleId, modulesLookup);
					item.phrase = getLabel(item.phraseId, phrasesLookup);
					Languages.forEach(lang => {
						if (item.languageCode === lang.languageCode) {
							item.language = lang.name;
							return;
						}
					});
				});
				
				setRows(theRows);
			});
	}, [phrasesLookup, modulesLookup, phrasesIndex.loading, modulesIndex.loading]);
	
	return <DataGridPro
		checkboxSelection={stage === "kizidev"}
		onRowSelectionModelChange={onSelectionModelChange}
		rows={rows ?? []}
		columns={columns}
		getRowId={row => row.id}
		initialState={{
			columns: {
				columnVisibilityModel: {
					creationDateTime: false
				},
			},
		}}
		slots={{
			footer: CustomFooterFactory(stage, rowSelectionModel, modules ?? [], phrases ?? [], rows ?? [], updateGrid, (items) => {
				setRows(items);
			})
		}}
	/>;
}

async function translationItemExists(translationItem: TranslationItem): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_TRANSLATION_ITEM,
			variables: {
				stage: translationsEditorTargetStage,
				phraseId: translationItem.phraseId,
				moduleId: translationItem.moduleId,
				languageCode: translationItem.languageCode
			},
			fetchPolicy: "no-cache"
		}
	);
	const nodes: any[] = existsItems.data.translationItems.nodes;
	return nodes.length > 0;
}

async function saveChanges(translationItem: TranslationItem) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_TRANSLATION_ITEM,
		variables: {
			stage: translationsEditorTargetStage,
			translation: cloneMutationData(translationItem),
		}
	});
}

async function getTranslationItems(): Promise<TranslationItem[]> {
	const result: TranslationItem[] = [];
	let after: string | null = null;
	let hasNextPage = false;
	do {
		const response: ApolloQueryResult<any> = await graphQLServices.translationsClient.query(
			{
				query: GET_TRANSLATION_ITEMS,
				variables: {
					stage: translationsEditorTargetStage,
					first: 500,
					after: after
				},
				fetchPolicy: "no-cache"
			}
		);
		
		if (response.data?.translationItems?.nodes === null)
			return result;
		
		after = response.data?.translationItems?.pageInfo?.endCursor;
		
		hasNextPage = response.data?.translationItems?.pageInfo?.hasNextPage === true;
		
		const nodes: any[] = response.data.translationItems.nodes;
		for (let i = 0; i < nodes.length; i++) {
			result.push(nodes[i]);
		}
		
	} while (hasNextPage && after !== null);
	return result;
}

function CustomFooterFactory(stage: string, selectedItems: GridRowId[], modules: Module[], phrases: PhraseItem[], translationItems: TranslationItem[], updateGrid: () => void, setRows: (items: TranslationItem[]) => void) {
	return function CustomFooter(props: any) {
		const [open, setOpen] = useState(false);
		
		const [translation, setTranslation] = useState("");
		const [moduleId, setModuleId] = useState("");
		const [languageCode, setLanguageCode] = useState("");
		const [phraseId, setPhraseId] = useState("");
		const [openApprove, setOpenApprove] = useState(false);
		const [approveConfirmation, setApproveConfirmation] = useState(false);
		const [approveCount, setApproveCount] = useState(0);
		
		const [openDelete, setOpenDelete] = useState(false);
		const [deleteConfirmation, setDeleteConfirmation] = useState(false);
		const [deleteCount, setDeleteCount] = useState(0);
		
		function handleClickOpen() {
			return setOpen(true);
		}
		
		function handleClose() {
			return setOpen(false);
		}
		
		async function handleCreate() {
			const id = crypto.randomUUID();
			const approvalDate = Instant.now().toString();
			const newTranslationItem = {
				id,
				moduleId,
				languageCode,
				phraseId,
				translation,
				approvalDate
			} as TranslationItem;
			await translationItemExists(newTranslationItem).then(async (x) => {
				if (x) {
					throw new Error(`Item already exists`);
				}
				
				await saveChanges(newTranslationItem);
				setOpen(false);
				updateGrid();
			});
		}
		
		function handleApprovalOpen() {
			const alreadyApproved: Set<string> = new Set<string>();
			translationItems.forEach((x) => {
				if (x.approvalDate)
					alreadyApproved.add(x.id);
			});
			
			for (let i = selectedItems.length - 1; i >= 0; i--) {
				if (alreadyApproved.has(selectedItems[i].toString()))
					selectedItems.splice(i, 1);
			}
			setOpenApprove(true);
		}
		
		function handleApproveClose() {
			setOpenApprove(false);
			setApproveCount(0);
			setApproveConfirmation(false);
		}
		
		function handleDeleteOpen() {
			setOpenDelete(true);
		}
		
		function handleDeleteClose() {
			setOpenDelete(false);
			setDeleteCount(0);
			setDeleteConfirmation(false);
		}
		
		
		async function handleApproval() {
			const items = selectedItems.map((x) => x.toString());
			const itemLookup: Set<string> = new Set<string>();
			items.forEach((x) => {
				itemLookup.add(x);
			});
			targetStageSafetyCheck(stage);
			const result = await graphQLServices.translationsClient.mutate({
				mutation: APPROVE_TRANSLATIONS,
				variables: {
					translationItemIds: items,
					stage
				}
			});
			setApproveCount(result.data.approveTranslations.approvalCount);
			setApproveConfirmation(true);
			
			if (result.data.approveTranslations.approvalDate) {
				translationItems.forEach((x) => {
					if (itemLookup.has(x.id))
						x.approvalDate = result.data.approveTranslations.approvalDate;
				});
			}
			
			updateGrid();
		}
		
		async function handleDelete() {
			const items = selectedItems.map((x) => x.toString());
			const itemLookup: Set<string> = new Set<string>();
			items.forEach((x) => {
				itemLookup.add(x);
			});
			targetStageSafetyCheck(stage);
			await graphQLServices.translationsClient.mutate({
				mutation: DELETE_TRANSLATION_ITEMS,
				variables: {
					translationItemIds: items,
					stage
				}
			});
			setDeleteCount(items.length);
			setDeleteConfirmation(true);
			
			const newRows: TranslationItem[] = [];
			for (let i = 0; i < translationItems.length; i++) {
				if (!itemLookup.has(translationItems[i].id))
					newRows.push(translationItems[i]);
			}
			setRows(newRows);
			updateGrid();
		}
		
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					variant={"contained"}
					onClick={handleClickOpen}
				>{translate("Add")}</Button>
				{selectedItems.length > 0 &&
					<Button
						key={"deleteTranslationsBtn"}
						variant={"contained"}
						onClick={handleDeleteOpen}
						disabled={stage !== translationsEditorTargetStage}
					>{translate("Delete")}</Button>}
				{selectedItems.length > 0 &&
					<Button
						key={"approveTranslationsBtn"}
						variant={"contained"}
						onClick={handleApprovalOpen}
						disabled={stage !== translationsEditorTargetStage}
					>{translate("Approve translations")}</Button>}
			</Stack>
			<Dialog open={openApprove} onClose={handleApproveClose}>
				<DialogTitle>{translate("Approve Translations")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{!approveConfirmation && translate("Approve {numberOfItems} translations?").replace("{numberOfItems}", selectedItems.length.toString())}
						
						{approveConfirmation && approveCount > 0 && translate("Successfully approved {approveCount} translations.").replace("{approveCount}", approveCount.toString())}
						
						{approveConfirmation && approveCount === 0 && translate("No translations approved")}
					
					</DialogContentText>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						{approveConfirmation && <Button onClick={handleApproveClose}>{translate("Close")}</Button>}
						{!approveConfirmation && <Button onClick={handleApproveClose}>{translate("Cancel")}</Button>}
						{!approveConfirmation &&
							<Button onClick={() => handleApproval()} color={"success"}>{translate("Approve")}</Button>}
					</Stack>
				</DialogContent>
			</Dialog>
			<Dialog open={openDelete} onClose={handleDeleteClose}>
				<DialogTitle>{translate("Delete Translations")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{!deleteConfirmation && translate("Delete {numberOfItems} translations?").replace("{numberOfItems}", selectedItems.length.toString())}
						
						{deleteConfirmation && deleteCount > 0 && translate("Successfully deleted {deleteCount} translations.").replace("{deleteCount}", deleteCount.toString())}
						
						{deleteConfirmation && deleteCount === 0 && translate("No translations deleted")}
					
					</DialogContentText>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						{deleteConfirmation && <Button onClick={handleDeleteClose}>{translate("Close")}</Button>}
						{!deleteConfirmation && <Button onClick={handleDeleteClose}>{translate("Cancel")}</Button>}
						{!deleteConfirmation &&
							<Button onClick={() => handleDelete()} color={"success"}>{translate("Delete")}</Button>}
					</Stack>
				</DialogContent>
			</Dialog>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New Phrase")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<form>
						<Autocomplete
							disablePortal
							options={Languages}
							autoSelect
							autoComplete
							renderInput={(params) => <TextField
								{...params}
								label={translate("Language")}
								variant={"standard"}
							/>}
							getOptionLabel={(it: any) => translate(it.name)}
							onChange={(event, item) => {
								setLanguageCode((item as Language).languageCode);
							}}
						/>
						<Autocomplete
							disablePortal
							options={modules}
							autoSelect
							autoComplete
							renderInput={(params) => <TextField
								{...params}
								label={translate("Module")}
								variant={"standard"}
							/>}
							getOptionLabel={(it: any) => translate(it.name)}
							onChange={(event, item) => {
								setModuleId(item?.id ?? "");
							}}
						/>
						
						<Autocomplete
							disablePortal
							options={phrases}
							autoSelect
							autoComplete
							renderInput={(params) => <TextField
								{...params}
								label={translate("Phrase")}
								variant={"standard"}
							/>}
							getOptionLabel={(it: any) => translate(it.phrase)}
							onChange={(event, item) => {
								setPhraseId(item?.id ?? "");
							}}
						/>
						
						<TextField
							margin="dense"
							label={translate("Translation")}
							fullWidth
							variant="standard"
							required
							value={translation}
							onChange={event => {
								setTranslation(event.target.value);
							}}
						/>
						<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
							<Button onClick={handleClose}>{translate("Cancel")}</Button>
							<div style={{flexGrow: 0, padding: "10px"}}>
								<ProcessButton
									defaultText={translate("Create")}
									inProgressText={translate("Saving")}
									completedText={translate("Saved")}
									resetKeys={[translation]}
									variant={"outlined"}
									onClick={handleCreate}/>
							</div>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} />
		</Stack>;
	};
}

function getLabel(id: string, items: Map<string, string> | undefined): string {
	return items?.get(id) ?? "";
}

export const translationsEditorDefaultTargetStage: EnvironmentStage = "dev";
